/**
 * Link Unfurling Utilities
 * Handles URL detection, validation, and processing for link previews
 */

// URL validation regex patterns
const URL_REGEX = /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g;
const SIMPLE_URL_REGEX = /^(https?:\/\/|www\.)/i;
const DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;

// Video platform regex patterns
const YOUTUBE_REGEX = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
const VIMEO_REGEX = /vimeo\.com\/(?:.*\/)?(\d+)/;
const YOUTUBE_SHORTS_REGEX = /youtube\.com\/shorts\/([^"&?\/\s]{11})/;

/**
 * Check if a string contains a URL
 * @param {string} text - Text to check for URLs
 * @returns {boolean} - True if text contains a URL
 */
export const containsUrl = (text) => {
  if (!text || typeof text !== 'string') return false;
  return URL_REGEX.test(text);
};

/**
 * Extract all URLs from a text string
 * @param {string} text - Text to extract URLs from
 * @returns {Array<string>} - Array of URLs found in the text
 */
export const extractUrls = (text) => {
  if (!text || typeof text !== 'string') return [];
  
  const matches = text.match(URL_REGEX);
  if (!matches) return [];
  
  return matches.map(url => normalizeUrl(url));
};

/**
 * Check if text looks like a URL
 * @param {string} text - Text to validate
 * @returns {boolean} - True if text appears to be a URL
 */
export const isUrl = (text) => {
  if (!text || typeof text !== 'string') return false;

  const trimmed = text.trim();

  // Don't treat single words as URLs
  if (!trimmed.includes('.') && !trimmed.includes('/')) {
    return false;
  }

  // Quick check for common URL patterns
  if (SIMPLE_URL_REGEX.test(trimmed)) {
    return true;
  }

  // Check for domain-like patterns
  if (DOMAIN_REGEX.test(trimmed)) {
    return true;
  }

  // More thorough check
  return URL_REGEX.test(trimmed);
};

/**
 * Normalize URL by adding protocol if missing
 * @param {string} url - URL to normalize
 * @returns {string} - Normalized URL
 */
export const normalizeUrl = (url) => {
  if (!url) return url;
  
  const trimmed = url.trim();
  
  // If it already has a protocol, return as is
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  
  // If it starts with www., add https://
  if (/^www\./i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  
  // If it looks like a domain, add https://
  if (URL_REGEX.test(trimmed)) {
    return `https://${trimmed}`;
  }
  
  return trimmed;
};

/**
 * Check if URL is a video platform URL
 * @param {string} url - URL to check
 * @returns {boolean} - True if URL is from a video platform
 */
export const isVideoUrl = (url) => {
  if (!url) return false;
  return YOUTUBE_REGEX.test(url) || VIMEO_REGEX.test(url) || YOUTUBE_SHORTS_REGEX.test(url);
};

/**
 * Extract video data from video platform URLs
 * @param {string} url - Video URL
 * @returns {Object|null} - Video data object or null if not a video URL
 */
export const extractVideoData = (url) => {
  if (!url) return null;

  // YouTube regular videos
  const youtubeMatch = url.match(YOUTUBE_REGEX);
  if (youtubeMatch) {
    return {
      platform: 'youtube',
      videoId: youtubeMatch[1],
      embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}`,
      thumbnailUrl: `https://img.youtube.com/vi/${youtubeMatch[1]}/maxresdefault.jpg`,
      type: 'video'
    };
  }

  // YouTube Shorts
  const shortsMatch = url.match(YOUTUBE_SHORTS_REGEX);
  if (shortsMatch) {
    return {
      platform: 'youtube',
      videoId: shortsMatch[1],
      embedUrl: `https://www.youtube.com/embed/${shortsMatch[1]}`,
      thumbnailUrl: `https://img.youtube.com/vi/${shortsMatch[1]}/maxresdefault.jpg`,
      type: 'shorts'
    };
  }
  
  // Vimeo
  const vimeoMatch = url.match(VIMEO_REGEX);
  if (vimeoMatch) {
    return {
      platform: 'vimeo',
      videoId: vimeoMatch[1],
      embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
      thumbnailUrl: null, // Vimeo thumbnails require API call
      type: 'video'
    };
  }
  
  return null;
};

/**
 * Replace URLs in text with clickable links
 * @param {string} text - Text containing URLs
 * @param {Function} linkRenderer - Function to render link elements
 * @returns {Array} - Array of text and link elements
 */
export const replaceUrlsWithLinks = (text, linkRenderer) => {
  if (!text || typeof text !== 'string') return [text];
  
  const urls = extractUrls(text);
  if (urls.length === 0) return [text];
  
  const parts = [];
  let lastIndex = 0;
  
  urls.forEach(url => {
    const index = text.indexOf(url, lastIndex);
    if (index > lastIndex) {
      parts.push(text.substring(lastIndex, index));
    }
    parts.push(linkRenderer(url));
    lastIndex = index + url.length;
  });
  
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts;
};

/**
 * Get domain from URL
 * @param {string} url - URL to extract domain from
 * @returns {string} - Domain name
 */
export const getDomain = (url) => {
  try {
    const urlObj = new URL(normalizeUrl(url));
    return urlObj.hostname.replace(/^www\./, '');
  } catch (error) {
    return url;
  }
};

/**
 * Check if URL is safe to preview (basic security check)
 * @param {string} url - URL to check
 * @returns {boolean} - True if URL appears safe
 */
export const isSafeUrl = (url) => {
  try {
    const urlObj = new URL(normalizeUrl(url));
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return false;
    }
    
    // Block localhost and private IP ranges
    const hostname = urlObj.hostname.toLowerCase();
    if (hostname === 'localhost' || 
        hostname === '127.0.0.1' ||
        hostname.startsWith('192.168.') ||
        hostname.startsWith('10.') ||
        hostname.startsWith('172.')) {
      return false;
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Debounce function for URL processing
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Cache for URL metadata to avoid repeated API calls
 */
class UrlMetadataCache {
  constructor(maxSize = 100, ttl = 5 * 60 * 1000) { // 5 minutes TTL
    this.cache = new Map();
    this.maxSize = maxSize;
    this.ttl = ttl;
  }

  get(url) {
    const item = this.cache.get(url);
    if (!item) return null;
    
    if (Date.now() - item.timestamp > this.ttl) {
      this.cache.delete(url);
      return null;
    }
    
    return item.data;
  }

  set(url, data) {
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    
    this.cache.set(url, {
      data,
      timestamp: Date.now()
    });
  }

  clear() {
    this.cache.clear();
  }
}

export const urlMetadataCache = new UrlMetadataCache();
